import { ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { AssetManagement, Header } from '@aptsi-types';
import { AssetsViewService } from '../../shared/services/assets-view.service';

type VerificationRecord = AssetManagement & Record<string, unknown>;

interface VerificationPaginationMeta {
  totalSystemAssets?: number;
  totalAssets?: number;
  totalVerificationResults?: number;
  totalPages?: number;
  currentPage?: number;
}

interface NormalizedVerificationResponse {
  rows: AssetManagement[];
  metadata?: VerificationPaginationMeta;
}

@Component({
  selector: 'app-system-assets-verification-view',
  templateUrl: './system-assets-verification-view.component.html',
  styleUrls: ['./system-assets-verification-view.component.scss']
})
export class SystemAssetsVerificationViewComponent implements OnInit {
  readonly itemsPerPage = 10;

  currentPage = 1;
  totalPagesNormal = 0;
  totalPagesSearch = 0;
  totalItemsNormal = 0;
  totalItemsSearch = 0;

  isLoading = true;
  isSearching = false;
  loadError = false;
  searchError = false;
  issearch = false;

  searchText = '';
  filterByHeader = 'all';

  filteredData: AssetManagement[] = [];
  searchedData: AssetManagement[] = [];

  private cachedTableSource: AssetManagement[] | null = null;
  private cachedTableData: AssetManagement[] = [];

  /** Ignore responses from requests that are no longer current. */
  private requestSequence = 0;

  readonly headers: Header[] = [
    {
      name: 'Asset Name',
      property: 'assetName',
      showInDropdown: true,
      description: 'Name of the system asset checked during verification'
    },
    {
      name: 'Endpoint',
      property: 'endpoint',
      showInDropdown: true,
      description: 'Endpoint checked during verification'
    },
    {
      name: 'Status',
      property: 'status',
      showInDropdown: true,
      description: 'Latest verification status'
    },
    {
      name: 'Response Code',
      property: 'responseCode',
      showInDropdown: true,
      description: 'HTTP response code returned by the endpoint'
    },
    {
      name: 'Last Checked At',
      property: 'lastCheckedAt',
      showInDropdown: false,
      description: 'Date and time when the asset was last checked'
    },
    {
      name: 'Last Updated',
      property: 'updatedAt',
      showInDropdown: false,
      description: 'Date and time when this verification record was updated'
    }
  ];

  // Verification history is read-only; keep all shared-table row actions hidden.
  readonly buttonConfig = {
    update: false,
    delete: false,
    import: false,
    configure: false,
    view: false
  };

  constructor(
    private readonly assetsViewService: AssetsViewService,
    private readonly router: Router,
    private readonly changeDetector: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    void this.fetchVerificationResults();
  }

  get displayedData(): AssetManagement[] {
    return this.issearch ? this.searchedData : this.filteredData;
  }

  get currentTotalItems(): number {
    return this.issearch ? this.totalItemsSearch : this.totalItemsNormal;
  }

  get currentTotalPages(): number {
    return this.issearch ? this.totalPagesSearch : this.totalPagesNormal;
  }

  /**
   * app-main-table reads cells directly from header.property. Format the date
   * fields here and keep a stable array reference between data changes.
   */
  get tableData(): AssetManagement[] {
    const source = this.displayedData;
    if (source === this.cachedTableSource) {
      return this.cachedTableData;
    }

    this.cachedTableData = source.map((asset) => {
      const row: Record<string, unknown> = {
        ...(asset as unknown as Record<string, unknown>)
      };

      row['lastCheckedAt'] = this.formatDateTime(asset.lastCheckedAt);
      row['updatedAt'] = this.formatDateTime(asset.updatedAt);
      row['endpoint'] = this.displayValue(asset.endpoint);
      row['assetName'] = this.displayValue(asset.assetName);
      row['status'] = this.displayValue(asset.status);
      row['responseCode'] = this.displayValue(asset.responseCode);

      return row as unknown as AssetManagement;
    });

    this.cachedTableSource = source;
    return this.cachedTableData;
  }

  async fetchVerificationResults(): Promise<void> {
    const requestId = ++this.requestSequence;
    this.isLoading = true;
    this.isSearching = false;
    this.loadError = false;
    this.searchError = false;

    try {
      const apiResponse: unknown = await this.assetsViewService
        .FetchSystemAssetsVerificationView(this.currentPage - 1)
        .toPromise();

      if (requestId !== this.requestSequence) {
        return;
      }

      const normalized = this.normalizeResponse(apiResponse);
      const metadata = normalized.metadata ?? this.findMetadata(normalized.rows);
      const total = metadata?.totalVerificationResults ?? metadata?.totalSystemAssets ?? metadata?.totalAssets;

      this.totalItemsNormal = this.readNonNegativeNumber(total, normalized.rows.length);
      // Use the page count supplied by the backend; do not derive it from the item count.
      this.totalPagesNormal = this.readNonNegativeNumber(metadata?.totalPages, 0);

      if (this.totalPagesNormal === 0) {
        this.currentPage = 1;
      } else if (this.currentPage > this.totalPagesNormal) {
        // If records were removed and the current page is no longer valid, reload the last valid page.
        this.currentPage = this.totalPagesNormal;
        await this.fetchVerificationResults();
        return;
      }

      this.filteredData = normalized.rows;
      this.cachedTableSource = null;
      console.debug('[SystemAssetsVerificationView] rows loaded:', this.filteredData.length);
    } catch (error) {
      if (requestId !== this.requestSequence) {
        return;
      }

      console.error('Error fetching system asset verification results:', error);
      this.loadError = true;
      this.filteredData = [];
      this.totalItemsNormal = 0;
      this.totalPagesNormal = 0;
      this.currentPage = 1;
      this.cachedTableSource = null;
    } finally {
      if (requestId === this.requestSequence) {
        this.isLoading = false;
        this.isSearching = false;
        this.refreshView();
      }
    }
  }

  async searchVerificationResults(): Promise<void> {
    const query = this.searchText.trim();
    if (!query) {
      await this.clearSearch();
      return;
    }

    this.searchText = query;
    this.issearch = true;
    this.currentPage = 1;
    this.totalItemsSearch = 0;
    this.totalPagesSearch = 0;
    await this.fetchVerificationResultsSearch();
  }

  async fetchVerificationResultsSearch(): Promise<void> {
    if (!this.searchText.trim()) {
      await this.clearSearch();
      return;
    }

    const requestId = ++this.requestSequence;
    this.isSearching = true;
    this.isLoading = false;
    this.loadError = false;
    this.searchError = false;

    try {
      const apiResponse: unknown = await this.assetsViewService
        .FetchSystemAssetsVerificationViewSearch(
          this.currentPage - 1,
          this.searchText.trim(),
          this.filterByHeader
        )
        .toPromise();

      if (requestId !== this.requestSequence) {
        return;
      }

      const normalized = this.normalizeResponse(apiResponse);
      // The search endpoint performs server-side filtering; do not filter again here.
      const metadata = normalized.metadata ?? this.findMetadata(normalized.rows);
      const total = metadata?.totalVerificationResults ?? metadata?.totalSystemAssets ?? metadata?.totalAssets;

      this.totalItemsSearch = this.readNonNegativeNumber(total, normalized.rows.length);
      // Use the backend page count for search results as well.
      this.totalPagesSearch = this.readNonNegativeNumber(metadata?.totalPages, 0);

      if (this.totalPagesSearch === 0) {
        this.currentPage = 1;
      } else if (this.currentPage > this.totalPagesSearch) {
        this.currentPage = this.totalPagesSearch;
        await this.fetchVerificationResultsSearch();
        return;
      }

      this.searchedData = normalized.rows;
      this.cachedTableSource = null;
      console.debug('[SystemAssetsVerificationView] search rows loaded:', this.searchedData.length);
    } catch (error) {
      if (requestId !== this.requestSequence) {
        return;
      }

      console.error('Error searching system asset verification results:', error);
      this.searchError = true;
      this.searchedData = [];
      this.totalItemsSearch = 0;
      this.totalPagesSearch = 0;
      this.currentPage = 1;
      this.cachedTableSource = null;
    } finally {
      if (requestId === this.requestSequence) {
        this.isSearching = false;
        this.isLoading = false;
        this.refreshView();
      }
    }
  }

  clearSearch(): Promise<void> {
    this.searchText = '';
    this.issearch = false;
    this.searchError = false;
    this.currentPage = 1;
    this.searchedData = [];
    this.totalItemsSearch = 0;
    this.totalPagesSearch = 0;
    this.cachedTableSource = null;
    return this.fetchVerificationResults();
  }

  onInputChange(event: Event): void {
    this.searchText = (event.target as HTMLInputElement).value;
  }

  onFilterChange(): void {
    this.currentPage = 1;
    this.totalItemsSearch = 0;
    this.totalPagesSearch = 0;
    this.searchError = false;
    if (this.searchText.trim()) {
      this.issearch = true;
      void this.fetchVerificationResultsSearch();
    } else {
      this.issearch = false;
      void this.fetchVerificationResults();
    }
  }

  changePage(page: number): void {
    const activeTotalPages = this.issearch ? this.totalPagesSearch : this.totalPagesNormal;
    if (
      !Number.isInteger(page) ||
      page < 1 ||
      page > activeTotalPages ||
      page === this.currentPage
    ) {
      return;
    }

    this.currentPage = page;
    if (this.issearch) {
      void this.fetchVerificationResultsSearch();
    } else {
      void this.fetchVerificationResults();
    }
  }

  retryCurrentView(): void {
    if (this.issearch) {
      void this.fetchVerificationResultsSearch();
    } else {
      void this.fetchVerificationResults();
    }
  }

  backToAssetsView(): void {
    void this.router.navigate(['/asset-management/auth-tools/assets-view']);
  }

  private normalizeResponse(raw: unknown): NormalizedVerificationResponse {
    if (Array.isArray(raw)) {
      const rows = raw.filter(
        (item): item is AssetManagement => item !== null && typeof item === 'object'
      );
      return { rows, metadata: this.findMetadata(rows) };
    }

    if (raw !== null && typeof raw === 'object') {
      const wrapper = raw as Record<string, unknown>;
      const candidateKeys = ['verificationView', 'verificationResults', 'assetview', 'data', 'items', 'content', 'results', 'assets', 'records'];

      for (const key of candidateKeys) {
        const value = wrapper[key];
        if (Array.isArray(value)) {
          const rows = value.filter(
            (item): item is AssetManagement => item !== null && typeof item === 'object'
          );
          const explicitMetadata = wrapper['metadata'] ?? wrapper['meta'];
          const paginationKeys = [
            'totalVerificationResults',
            'totalSystemAssets',
            'totalAssets',
            'totalPages',
            'currentPage'
          ];
          const hasTopLevelPagination = paginationKeys.some(
            key => wrapper[key] !== undefined && wrapper[key] !== null
          );

          return {
            rows,
            metadata: (
              explicitMetadata ??
              (hasTopLevelPagination ? wrapper : this.findMetadata(rows))
            ) as VerificationPaginationMeta | undefined
          };
        }
      }

      if (wrapper['assetId'] || wrapper['assetName'] || wrapper['status'] || wrapper['responseCode']) {
        const rows = [raw as AssetManagement];
        return { rows, metadata: this.findMetadata(rows) };
      }
    }

    console.warn('[SystemAssetsVerificationView] Unrecognized API response structure:', raw);
    return { rows: [] };
  }

  private findMetadata(rows: AssetManagement[]): VerificationPaginationMeta | undefined {
    const metadataRow = rows.find((row) => {
      const record = row as VerificationRecord;
      return record['totalVerificationResults'] !== undefined ||
        record['totalSystemAssets'] !== undefined ||
        record['totalAssets'] !== undefined ||
        record['totalPages'] !== undefined ||
        record['currentPage'] !== undefined;
    });
    return metadataRow as VerificationPaginationMeta | undefined;
  }

  private displayValue(value: unknown): string {
    return value === null || value === undefined || String(value).trim() === '' ? '—' : String(value);
  }

  private formatDateTime(value: unknown): string {
    if (value === null || value === undefined || String(value).trim() === '') {
      return '—';
    }

    const date = new Date(String(value));
    if (Number.isNaN(date.getTime())) {
      return String(value);
    }

    return date.toLocaleString(undefined, {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  }

  private readNonNegativeNumber(value: unknown, fallback: number): number {
    if (value === null || value === undefined || value === '') {
      return fallback;
    }
    const parsed = Number(value);
    return Number.isFinite(parsed) && parsed >= 0 ? parsed : fallback;
  }

  private refreshView(): void {
    this.changeDetector.detectChanges();
  }
}
